import { useId, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { map } from '../core/MapView';
import { useAttributePreference } from '../../common/util/preferences';
import { useMapControlSetting } from '../controls/useMapControls';
import CircularBuffer from '../../common/util/CircularBuffer';

// Cor azul para dispositivos em movimento
const MOVING_COLOR = '#2196F3';

const MapLiveRoutes = ({ deviceIds }) => {
  const id = useId();

  const theme = useTheme();

  const userType = useAttributePreference('mapLiveRoutes', 'none');

  // Map controls panel settings - usando hook individual para melhor performance
  const showTailSetting = useMapControlSetting('showTail', userType !== 'none');
  const showTail = showTailSetting ?? (userType !== 'none');

  // Se showTail é forçado true (via URL follow=true), usar 'selected' como tipo
  // Isso permite ativar o trail mesmo se mapLiveRoutes está em 'none'
  const type = showTail && userType === 'none' ? 'selected' : userType;

  // 2ECOMP: shallowEqual evita re-renders quando objetos não mudaram estruturalmente
  const devices = useSelector((state) => state.devices.items, shallowEqual);
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);
  const positions = useSelector((state) => state.session.positions, shallowEqual);
  const history = useSelector((state) => state.session.history, shallowEqual);

  const mapLineWidth = useAttributePreference('mapLineWidth', 2);
  const mapLineOpacity = useAttributePreference('mapLineOpacity', 1);

  useEffect(() => {
    if (type !== 'none') {
      map.addSource(id, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        },
      });
      map.addLayer({
        source: id,
        id,
        type: 'line',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['get', 'width'],
          'line-opacity': ['get', 'opacity'],
        },
      });

      return () => {
        if (map.getLayer(id)) {
          map.removeLayer(id);
        }
        if (map.getSource(id)) {
          map.removeSource(id);
        }
      };
    }
    return () => {};
  }, [type]);

  useEffect(() => {
    if (type !== 'none') {
      const visibleIds = deviceIds
        .filter((id) => (type === 'selected' ? id === selectedDeviceId : true))
        .filter((id) => history.hasOwnProperty(id) && !CircularBuffer.isEmpty(history[id]));

      // 2ECOMP DEBUG: Log trail updates
      if (visibleIds.length > 0) {
        const totalPoints = visibleIds.reduce((sum, devId) => sum + (history[devId]?.length || 0), 0);
        // console.log(`[Trail] Updating ${visibleIds.length} trails with ${totalPoints} total points`);
      }

      map.getSource(id)?.setData({
        type: 'FeatureCollection',
        features: visibleIds.map((deviceId) => {
          const position = positions[deviceId];
          const isMoving = position?.attributes?.motion === true;
          const device = devices[deviceId];

          // Prioridade: 1) cor customizada, 2) azul se movendo, 3) cor padrão do tema
          let color;
          if (device?.attributes?.['web.reportColor']) {
            color = device.attributes['web.reportColor'];
          } else if (isMoving && device?.status === 'online') {
            color = MOVING_COLOR;
          } else {
            color = theme.palette.geometry.main;
          }

          // 2ECOMP: Filtrar coordenadas inválidas (null, NaN) que podem causar erro no MapLibre
          const coordinates = CircularBuffer.toArray(history[deviceId])
            .filter((coord) => Array.isArray(coord)
              && coord.length >= 2
              && Number.isFinite(coord[0])
              && Number.isFinite(coord[1]));

          return {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates,
            },
            properties: {
              color: color || theme.palette.geometry.main,
              // 2ECOMP: Garantir que width e opacity são números válidos
              width: Number.isFinite(mapLineWidth) ? mapLineWidth : 2,
              opacity: Number.isFinite(mapLineOpacity) ? mapLineOpacity : 1,
            },
          };
        }),
      });
    }
  }, [theme, type, devices, positions, selectedDeviceId, history, deviceIds]);

  // Toggle tail visibility based on control panel settings
  useEffect(() => {
    if (type === 'none' || !map) return;
    try {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', showTail ? 'visible' : 'none');
      }
    } catch {
      // Layer not yet created
    }
  }, [id, type, showTail]);

  return null;
};

export default MapLiveRoutes;
